import { useSyncExternalStore } from 'react';

import type { FileBody } from '@/shared/api/generated/model';

/** Что открыто в просмотрщике. */
export interface ViewerState {
  files: FileBody[];
  index: number;
  /** Можно ли удалить файл; вызывается для текущего. */
  canRemove?: ((file: FileBody) => boolean) | undefined;
  onRemove?: ((file: FileBody) => Promise<void>) | undefined;
}

let state: ViewerState | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Просмотрщик — один на приложение и открывается откуда угодно: из
 * вложений, из комментария, из картинки в описании. Поэтому не контекст, а
 * маленькое хранилище: `openViewer` вызывают, `FileViewerHost` показывает.
 */
export function openViewer(next: ViewerState): void {
  state = next;
  emit();
}

export function closeViewer(): void {
  state = null;
  emit();
}

export function stepViewer(delta: number): void {
  if (state === null || state.files.length === 0) {
    return;
  }

  const length = state.files.length;

  state = { ...state, index: (state.index + delta + length) % length };
  emit();
}

/** Файл удалили — убрать из ленты, не закрывая просмотрщик. */
export function dropFromViewer(id: string): void {
  if (state === null) {
    return;
  }

  const files = state.files.filter((f) => f.id !== id);

  if (files.length === 0) {
    state = null;
  } else {
    state = { ...state, files, index: Math.min(state.index, files.length - 1) };
  }

  emit();
}

export function useViewer(): ViewerState | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    () => state,
    () => null,
  );
}
